import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, TouchableOpacity } from 'react-native';

import { ProjectChecklistCenterModal } from '@components/ProjectDetail/ProjectChecklist/ProjectChecklistCenterModal';
import { Box, Text } from '@components/Restyle/index';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import theme from '@themes/theme';

export const ChecklistCommonModal = ({
  visible,
  skippedItems,
  onClose,
  onPressDelete,
  title,
  message,
  buttonTitle,
  messageEnd,
  isLoading = false,
}: {
  title?: string;
  buttonTitle?: string;
  message?: string;
  messageEnd?: string;
  visible: boolean;
  skippedItems: string[];
  onClose: () => void;
  onPressDelete?: () => void;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();

  // Locks the modal while a confirmed action (e.g. delete) is in flight —
  // otherwise the X button/backdrop-close would let a user dismiss before
  // the server confirms.
  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const getSkippedItemsText = (
    skippedItems: string[],
    index: number
  ): string => {
    if (index >= 3) {
      const othersCount = skippedItems.length - 3;
      return `and ${othersCount} other${othersCount > 1 ? 's' : ''}`;
    }
    return '';
  };

  return (
    <ProjectChecklistCenterModal
      contentNode={
        <Box m='l'>
          <Box flexDirection='row' marginBottom='l' alignItems='center'>
            <Text
              flex={1}
              style={{ alignSelf: 'flex-start' }}
              variant='labelLarge'
              color='textPrimary'>
              {title ?? t('models:projects.checklist.alreadyInChecklist')}
            </Text>
            <TouchableOpacity
              style={{ outline: 'none' }}
              disabled={isLoading}
              onPress={handleClose}>
              <Icon name='X' variant='m' />
            </TouchableOpacity>
          </Box>

          {/* TA-10949 #13/#14: per design, a no-list delete (single task)
              renders message + messageEnd as ONE text block joined by a
              line break — no paragraph gap between them, just the normal
              line-height. Only the multi-select case (list present)
              splits them into separate Text elements, each `m` apart from
              the list between them. Keeping the single-task case as one
              Text is what the plain '\n' below reproduces; two separate
              Text elements each with their own margin would always add
              up to a full margin's worth of gap, never zero. */}
          {skippedItems.length > 0 ? (
            <>
              <Text
                variant='webBodySecondary'
                color='textPrimary'
                style={{ alignSelf: 'flex-start' }}>
                {message ??
                  t('models:projects.checklist.theseItemsWereSkipped')}
              </Text>

              <ScrollView
                style={{
                  maxHeight: 80,
                  marginTop: theme.spacing.m,
                }}>
                {skippedItems.slice(0, 4).map((item, index) => (
                  <Box
                    mx='xs'
                    key={item + index.toString()}
                    flexDirection='row'
                    alignItems='flex-start'>
                    <Text
                      marginRight='xs'
                      variant='webBodySecondary'
                      color='textPrimary'
                      style={{ alignSelf: 'flex-start' }}>
                      •
                    </Text>
                    <Text
                      mr='xs'
                      flex={1}
                      numberOfLines={1}
                      ellipsizeMode='tail'
                      flexShrink={1}
                      variant='webBodySecondary'
                      color='textPrimary'
                      style={{ alignSelf: 'flex-start' }}>
                      {index < 3
                        ? item
                        : getSkippedItemsText(skippedItems, index)}
                    </Text>
                  </Box>
                ))}
              </ScrollView>

              {messageEnd && (
                <Text
                  marginTop='m'
                  variant='webBodySecondary'
                  color='textPrimary'
                  style={{ alignSelf: 'flex-start' }}>
                  {messageEnd}
                </Text>
              )}
            </>
          ) : (
            <Text
              variant='webBodySecondary'
              color='textPrimary'
              style={{ alignSelf: 'flex-start' }}>
              {message ?? t('models:projects.checklist.theseItemsWereSkipped')}
              {messageEnd ? `\n${messageEnd}` : null}
            </Text>
          )}

          <Box style={{ alignSelf: 'flex-end' }} width={125} mt='l'>
            <Button
              height={48}
              fullWidth={true}
              variant='primary'
              isLoading={isLoading}
              onPress={onPressDelete ?? handleClose}
              accessibilityLabel={
                buttonTitle ?? t('models:projects.checklist.gotIt')
              }>
              <Text variant='labelSmall' color='white'>
                {buttonTitle ?? t('models:projects.checklist.gotIt')}
              </Text>
            </Button>
          </Box>
        </Box>
      }
      onClose={handleClose}
      visible={visible}
    />
  );
};
